import { isEqual, noop, omit } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FilterDropdownItem,
  FilterToolbarGroup,
  SelectedFilters,
} from 'src/components/Filters';
import { QuickFilterToolbarItem } from 'src/components/FilterToolbar/QuickFilterToolbarItem';
import { useDefaultPurchaseFilters } from 'src/components/Purchases/PurchaseFilterBar/hooks/useDefaultPurchaseFilters';
import { useFilterToolbarQuickFilters } from 'src/hooks/useQuickFilters';
import {
  DefaultPurchaseQuery
} from 'src/utils/eventQueryUtils';
import {
  CatalogResults,
  EventWithData,
  PurchaseOrderQuery,
  TicketGroupMetrics,
  UserSetting,
} from 'src/WebApiController';

import { useFilterQueryContext } from '../FilterQueryContext';
import { defaultEventSortingCriteria } from './MobileInventorySortAndFilterContext.constants';
import {
  InventoryEventSortingCriteria,
  InventorySortingCriteria,
} from './MobileInventorySortAndFilterContext.types';
import { sortEventWithCriteria } from './MobileInventorySortAndFilterContext.utils';
import { useMobilePurchasesFilters } from './MobilePurchasesSortAndFilterContext.hooks';

export type IMobilePurchasesSortAndFilterContext = {
  // Sorting props
  sortingCriteria: InventorySortingCriteria;
  hasSortingChanged: boolean;
  setSortingCriteria: (_: InventorySortingCriteria) => void;
  withEventSortingCriteria: (
    events: EventWithData[],
    eventMetrics?: Record<string, TicketGroupMetrics>,
    catalogResult?: CatalogResults | undefined
  ) => EventWithData[];
  // Filter props
  tempQuery: PurchaseOrderQuery;
  filters: FilterToolbarGroup[];
  selectedFilters: SelectedFilters;
  quickFilters?: QuickFilterToolbarItem[] | undefined;
  hasFilterApplied: boolean;
  canSaveFilter: boolean;
  setTempQuery: (_: PurchaseOrderQuery) => void;
  onFilterRemoved: (_: FilterDropdownItem) => void;
  onSaveSelectedFilters: (_: SelectedFilters) => void;
  onSaveFilterQuery: () => void;
  onResetFilters: () => void;
  onResetCustomFilter: () => void;
};

const MobilePurchasesSortAndFilterContext =
  createContext<IMobilePurchasesSortAndFilterContext>({
    sortingCriteria: {} as InventorySortingCriteria,
    setSortingCriteria: noop,
    withEventSortingCriteria: (
      _: EventWithData[],
      __?: Record<string, TicketGroupMetrics>,
      ___?: CatalogResults | undefined
    ) => [],
    hasSortingChanged: false,
    filters: [],
    selectedFilters: {},
    tempQuery: DefaultPurchaseQuery,
    hasFilterApplied: false,
    canSaveFilter: false,
    setTempQuery: noop,
    onFilterRemoved: noop,
    onSaveSelectedFilters: noop,
    onSaveFilterQuery: noop,
    onResetFilters: noop,
    onResetCustomFilter: noop,
  });

export const useMobilePurchasesSortAndFilterContext = () =>
  useContext(MobilePurchasesSortAndFilterContext);

export const MobilePurchasesSortAndFilterContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [sortingCriteria, setSortingCriteria] =
    useState<InventorySortingCriteria>(defaultEventSortingCriteria);

  const withEventSortingCriteria = useCallback(
    (
      events: EventWithData[],
      eventMetrics?: Record<string, TicketGroupMetrics>,
      catalogResult?: CatalogResults | undefined
    ) => {
      const { sorting } = sortingCriteria as InventoryEventSortingCriteria;
      return events.toSorted(
        sortEventWithCriteria(sorting, eventMetrics, catalogResult)
      );
    },
    [sortingCriteria]
  );

  const hasSortingChanged = useMemo(() => {
    return !isEqual(sortingCriteria, defaultEventSortingCriteria);
  }, [sortingCriteria]);

  const {
    filterQuery,
    initialQuery,
    tempQuery,
    setFilterQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<PurchaseOrderQuery>();

  useEffect(() => {
    if (!isEqual(filterQuery, tempQuery)) {
      setTempQuery(filterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const defaultQuickFilters = useDefaultPurchaseFilters({
    initialQuery,
    purchaseQuery: filterQuery,
  });

  const { quickFilters, onResetAll } = useFilterToolbarQuickFilters({
    quickFiltersStateSetting: UserSetting.QuickFiltersStatePurchases,
    customQuickFiltersSetting: UserSetting.QuickFiltersCustomPurchases,
    defaultQuickFilters,
    currentQuery: filterQuery,
    initialQuery,
    onSelect: ({ query }) => {
      setFilterQuery(query);
    },
  });

  const { filters, selectedFilters, onFilterRemoved, onSaveSelectedFilters } =
    useMobilePurchasesFilters(
      initialQuery,
      filterQuery,
      tempQuery,
      setTempQuery,
      resetTempQuery
    );

  const onResetFilters = useCallback(() => {
    onSaveSelectedFilters({});
    setTempQuery(initialQuery);
  }, [initialQuery, onSaveSelectedFilters, setTempQuery]);

  const onSaveFilterQuery = useCallback(() => {
    setFilterQuery(tempQuery);
  }, [setFilterQuery, tempQuery]);

  const hasFilterApplied = useMemo(() => {
    const filteredFilters = omit(filterQuery, [
      'sortBy',
      'timezoneOffsetMins',
      'viewMode',
      'eventOrMappingIds',
      'eventTimeFrameFilter',
    ]);
    const filteredDefaultFilters = omit(DefaultPurchaseQuery, [
      'sortBy',
      'timezoneOffsetMins',
      'viewMode',
      'eventOrMappingIds',
      'eventTimeFrameFilter',
    ]);
    return !isEqual(filteredFilters, filteredDefaultFilters);
  }, [filterQuery]);

  const canSaveFilter = useMemo(
    () => !isEqual(filterQuery, tempQuery),
    [filterQuery, tempQuery]
  );

  return (
    <MobilePurchasesSortAndFilterContext.Provider
      value={{
        sortingCriteria,
        hasSortingChanged,
        setSortingCriteria,
        withEventSortingCriteria,
        tempQuery,
        filters,
        quickFilters,
        selectedFilters,
        hasFilterApplied,
        canSaveFilter,
        setTempQuery,
        onFilterRemoved,
        onSaveSelectedFilters,
        onSaveFilterQuery,
        onResetFilters,
        onResetCustomFilter: onResetAll,
      }}
    >
      {children}
    </MobilePurchasesSortAndFilterContext.Provider>
  );
};
